import React, { useState, useMemo, useCallback, useEffect, useRef, useContext } from "react";
import { Dialog, DialogTitle, DialogContent, Tabs, Tab, Box, Button, Typography, CircularProgress, useTheme, TextField, InputAdornment, IconButton, Chip, Tooltip } from "@material-ui/core";
import { useChangelog } from "../../context/Changelog/ChangelogContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import Markdown from "markdown-to-jsx";
import MarkdownWrapper from "../MarkdownWrapper";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CloseIcon from "@material-ui/icons/Close";
import ScheduleIcon from "@material-ui/icons/Schedule";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import AttachmentIcon from "@material-ui/icons/AttachFile";
import WhatshotIcon from "@material-ui/icons/Whatshot"; // para "breaking"
import SecurityIcon from "@material-ui/icons/Security"; // para "security"
import StarIcon from "@material-ui/icons/Star";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// Utilitário local para gerar cores rgba a partir de um hex
const hexToRgba = (hex, alpha = 1) => {
  try {
    const clean = hex.replace('#', '');
    const bigint = parseInt(clean, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch {
    return hex;
  }
};

// Card de release com itens e prévias de anexos
const ReleaseCard = ({ rel, expanded, toggle, markRead }) => {
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const backend = process.env.REACT_APP_BACKEND_URL;

  // Ordem de prioridade das categorias para exibição dos itens
  const CATEGORY_ORDER = [
    'breaking',
    'security',
    'feature',
    'improvement',
    'fix',
    'deprecated',
    'internal'
  ];
  const getCatRank = (c) => {
    const key = String(c || '').toLowerCase();
    const idx = CATEGORY_ORDER.indexOf(key);
    return idx === -1 ? CATEGORY_ORDER.length : idx;
  };

  const buildPublicUrl = useCallback((p) => {
    if (!p) return null;
    const hasCompany = /^company\d+\//.test(p);
    const path = hasCompany ? p : (user?.companyId ? `company${user.companyId}/${p}` : p);
    return `${backend}/public/${path}`;
  }, [backend, user?.companyId]);

  const withImgFallback = (imgEl, originalPath) => {
    if (!imgEl || !originalPath) return;
    try {
      const hasCompany = /^company\d+\//.test(originalPath);
      if (!hasCompany && user?.companyId) {
        const altUrl = `${backend}/public/company${user.companyId}/${originalPath}`;
        if (imgEl.src !== altUrl) imgEl.src = altUrl;
      }
    } catch {}
  };

  const renderCategoriaChip = (categoria) => {
    const cfgMap = {
      breaking: { label: 'Breaking', icon: <WhatshotIcon style={{ fontSize: 16 }} /> },
      security: { label: 'Segurança', icon: <SecurityIcon style={{ fontSize: 16 }} /> },
      feature: { label: 'Funcionalidade' },
      improvement: { label: 'Melhoria' },
      fix: { label: 'Correção' },
      deprecated: { label: 'Descontinuado' },
      internal: { label: 'Interno' },
    };
    const colorMap = {
      breaking: theme.palette.error.main,
      security: theme.palette.warning.main,
      feature: theme.palette.primary.main,
      improvement: theme.palette.info ? theme.palette.info.main : theme.palette.primary.main,
      fix: theme.palette.success.main,
      deprecated: theme.palette.grey[500],
      internal: theme.palette.secondary ? theme.palette.secondary.main : theme.palette.primary.main,
    };
    const cfg = cfgMap[categoria] || { label: categoria };
    const base = colorMap[categoria] || theme.palette.primary.main;
    const bg = theme.palette.type === 'dark' ? hexToRgba(base, 0.20) : hexToRgba(base, 0.10);
    return (
      <Chip
        size="small"
        label={cfg.label}
        icon={cfg.icon || null}
        style={{
          height: 20,
          background: bg,
          border: `1px solid ${base}`,
          color: base,
        }}
      />
    );
  };

  return (
    <Box mb={1.25} p={1} borderRadius={8} style={{ border: `1px solid ${theme.palette.divider}` }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" onClick={toggle} style={{ cursor: 'pointer' }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>{rel.version || ""}</Typography>
          {rel.titulo && (
            <Typography variant="body2" color="textSecondary">{rel.titulo}</Typography>
          )}
          {rel.naoLido && (
            <Chip size="small" color="secondary" label="novo" />
          )}
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 6 }} onClick={(e)=>e.stopPropagation()}>
          {rel.naoLido && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={async (e) => { e.stopPropagation(); try { await markRead(rel.id); } catch {} }}
            >
              Marcar como lido
            </Button>
          )}
          <IconButton size="small" onClick={toggle} aria-label={expanded ? 'Recolher' : 'Expandir'}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {expanded && (
        <Box mt={1}>
          {(() => {
            // Ordena itens por categoria conforme prioridade definida; estável dentro da mesma categoria
            const itensSorted = Array.isArray(rel.itens)
              ? rel.itens
                  .map((it, idx) => ({ it, idx }))
                  .sort((a, b) => {
                    const ra = getCatRank(a.it.categoria);
                    const rb = getCatRank(b.it.categoria);
                    if (ra !== rb) return ra - rb;
                    return a.idx - b.idx; // estabilidade
                  })
                  .map(x => x.it)
              : [];

            return itensSorted.map((it) => {
            const isImg = (p) => /(\.png|\.jpe?g|\.gif|\.webp|\.svg)$/i.test(String(p || '').toLowerCase());
            const attImgs = (it.attachments || []).filter(a => isImg(a.path));
            const otherFiles = (it.attachments || []).filter(a => !isImg(a.path));
            const legacyIsImage = it.anexoPath ? isImg(it.anexoPath) : false;
            const imagesUnified = [];
            if (legacyIsImage) imagesUnified.push({ path: it.anexoPath, isLegacy: true });
            attImgs.forEach(a => { if (!legacyIsImage || String(a.path) !== String(it.anexoPath)) imagesUnified.push({ path: a.path }); });
            const imageUrls = imagesUnified.map(a => ({ src: buildPublicUrl(a.path), title: it.titulo || 'Anexo' }));
            const legacyUrl = it.anexoPath ? buildPublicUrl(it.anexoPath) : null;

            return (
              <Box key={it.id} mb={1.25} p={1} borderRadius={6} style={{ background: theme.palette.type === 'dark' ? hexToRgba(theme.palette.primary.main, 0.06) : hexToRgba(theme.palette.primary.main, 0.035) }}>
                <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: it.titulo ? 6 : 0 }}>
                  {it.categoria && renderCategoriaChip(it.categoria)}
                  {it.titulo && <Typography variant="body2" style={{ fontWeight: 600 }}>{it.titulo}</Typography>}
                </Box>
                {it.descricaoMarkdown && (
                  <Box mb={0.5}>
                    <Markdown>{it.descricaoMarkdown}</Markdown>
                  </Box>
                )}

                {/* Galeria de imagens (anexos múltiplos) */}
                {/* Galeria de imagens unificada (principal + anexos) */}
                {imagesUnified.length > 0 && (
                  <Box display="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
                    {imagesUnified.map((img, idx) => {
                      const url = buildPublicUrl(img.path);
                      return (
                        <Box key={`${String(img.path)}-${idx}`} onClick={() => {
                          try {
                            const startIndex = Math.max(0, idx);
                            window.dispatchEvent(new CustomEvent('changelog-lightbox-open', { detail: { images: imageUrls, index: startIndex } }));
                          } catch {}
                        }} style={{ cursor: 'zoom-in' }}>
                          <img
                            src={url}
                            alt={it.titulo || 'Anexo'}
                            loading="lazy"
                            style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block', borderRadius: 6, boxShadow: theme.palette.type === 'dark' ? '0 0 0 1px rgba(255,255,255,0.05)' : '0 0 0 1px rgba(0,0,0,0.06)' }}
                            onError={e => withImgFallback(e?.currentTarget, img.path)}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Outros arquivos (PDF/MP4/etc) */}
                {otherFiles.length > 0 && (
                  <Box mt={0.5}>
                    {otherFiles.map((att, idx) => (
                      <Typography key={idx} variant="caption" component="div" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <AttachmentIcon style={{ fontSize: 15, opacity: 0.9 }} />
                        <a href={buildPublicUrl(att.path)} target="_blank" rel="noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 500 }}>Abrir anexo</a>
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Legado: anexo único (não-imagem) */}
                {legacyUrl && !legacyIsImage && (
                  <Box mt={it.descricaoMarkdown ? 0.5 : 0}>
                    <Typography variant="caption" component="div" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <AttachmentIcon style={{ fontSize: 15, opacity: 0.9 }} />
                      <a href={legacyUrl} target="_blank" rel="noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 500 }}>
                        Abrir anexo
                      </a>
                    </Typography>
                  </Box>
                )}
              </Box>
            );
            });
          })()}
          {(!rel.itens || rel.itens.length === 0) && (
            <Typography variant="caption" color="textSecondary">Sem itens.</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
const ReleasesSection = ({ onMarkAll }) => {
  const { releases, loading, hasMore, fetchMore, markRead } = useChangelog();
  const theme = useTheme();
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const [categoria, setCategoria] = useState("todas");
  const scrollRef = useRef(null);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const naoLidas = releases.filter(r => r.naoLido).length;

  const filtradas = useMemo(() => {
    return releases.filter(r => {
      if (search) {
        const s = search.toLowerCase();
        const hitRelease = (r.version||'').toLowerCase().includes(s) || (r.titulo||'').toLowerCase().includes(s);
        const hitItem = r.itens?.some(it => (it.titulo||'').toLowerCase().includes(s) || (it.descricaoMarkdown||'').toLowerCase().includes(s));
        if (!hitRelease && !hitItem) return false;
      }
      if (categoria !== 'todas') {
        if (!r.itens?.some(it => it.categoria === categoria)) return false;
      }
      return true;
    });
  }, [releases, search, categoria]);

  const grupos = useMemo(() => {
    const map = {};
    filtradas.forEach(r => {
      const d = r.releasedAt ? new Date(r.releasedAt) : null;
      const key = d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` : 'rascunhos';
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return Object.entries(map).sort((a,b)=> b[0].localeCompare(a[0]));
  }, [filtradas]);

  const formatGrupoLabel = (key) => {
    if (key === 'rascunhos') return 'Rascunhos';
    const [y,m] = key.split('-');
    const dt = new Date(Number(y), Number(m)-1, 1);
    return dt.toLocaleDateString('pt-BR', { month:'long', year:'numeric' });
  };

  const markAll = async () => {
    for (const r of releases) {
      if (r.naoLido) {
        try { await markRead(r.id); } catch {}
      }
    }
    if (onMarkAll) onMarkAll();
  };

  const CATEGORIAS = [
    { value:'todas', label:'Todas' },
    { value:'feature', label:'Funcionalidade' },
    { value:'improvement', label:'Melhoria' },
    { value:'fix', label:'Correção' },
    { value:'security', label:'Segurança' },
    { value:'breaking', label:'Breaking' },
    { value:'deprecated', label:'Descontinuado' },
    { value:'internal', label:'Interno' },
  ];

  return (
    <Box display="flex" flexDirection="column" height="100%" ref={scrollRef}>
      <Box mb={1} position="sticky" top={0} zIndex={2} style={{ background: theme.palette.background.paper, paddingBottom:8 }}>
        <Box display="flex" flexDirection="column" alignItems="center" style={{ gap:8 }}>
          <Box display="flex" alignItems="center" style={{ width:'100%', justifyContent:'center' }}>
            <Box style={{ width:'70%', minWidth:260, maxWidth:760, display:'flex', alignItems:'center', gap:8 }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Buscar texto ou versão"
                value={search}
                onChange={e=>setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={()=>setSearch("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ):null,
                  style: { fontSize:13, borderRadius: 24, paddingLeft:4, paddingRight:4 }
                }}
                InputLabelProps={{ style:{ borderRadius:24 } }}
                style={{ borderRadius:24, flex:1, minWidth:0 }}
              />
              {naoLidas > 0 && (
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  startIcon={<DoneAllIcon />}
                  onClick={markAll}
                  style={{ whiteSpace:'nowrap', flex:'none' }}
                >
                  Ler todos ({naoLidas})
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        <Box mt={1} display="flex" flexWrap="wrap" style={{ gap:6, justifyContent:'center' }}>
          {CATEGORIAS.map(cat => {
            const active = categoria === cat.value;
            const color = active ? theme.palette.primary.main : theme.palette.divider;
            return (
              <Chip
                key={cat.value}
                label={cat.label}
                size="small"
                onClick={()=> setCategoria(cat.value)}
                style={{
                  background: active ? hexToRgba(theme.palette.primary.main, theme.palette.type==='dark'?0.35:0.15) : 'transparent',
                  border: `1px solid ${color}`,
                  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                  height: 24,
                  fontSize:12,
                  cursor:'pointer'
                }}
                variant={active ? 'default':'outlined'}
              />
            );
          })}
        </Box>
      </Box>
      <Box flex={1} overflow="auto" pr={0.5} style={{ scrollbarGutter:'stable', paddingTop:4 }}>
        <Box style={{ maxWidth:760, margin:'0 auto' }}>
          {grupos.map(([key, arr]) => (
            <Box key={key} mb={3}>
              <Typography variant="overline" style={{ opacity:0.7, fontWeight:600, letterSpacing:1 }}>{formatGrupoLabel(key)}</Typography>
              <Box mt={0.5}>
                {arr.map(r => (
                  <ReleaseCard key={r.id} rel={r} expanded={!!expanded[r.id]} toggle={() => toggle(r.id)} markRead={markRead} />
                ))}
              </Box>
            </Box>
          ))}
          {loading && (
            <Box py={2} display="flex" justifyContent="center"><CircularProgress size={24} /></Box>
          )}
          {!loading && filtradas.length === 0 && (
            <Typography variant="body2" style={{ opacity:0.7, textAlign:'center', marginTop:32 }}>
              {releases.length === 0 ? 'Sem novidades no momento ✨' : 'Nada encontrado.'}
            </Typography>
          )}
          {!loading && hasMore && (
            <Box display="flex" justifyContent="center" mt={1.5}>
              <Button onClick={()=>fetchMore()} variant="outlined" size="small" disabled={loading}>Carregar mais</Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Quadro simples do Roadmap
const RoadmapBoard = () => {
  const theme = useTheme();
  const { roadmap } = useChangelog();

  const isNovo = (createdAt) => {
    if (!createdAt) return false;
    const created = new Date(createdAt).getTime();
    const now = Date.now();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return now - created <= sevenDays;
  };

  const columns = [
    { key: 'planned', label: 'Planejado', icon: <ScheduleIcon style={{ width:18, height:18 }} /> },
    { key: 'in_progress', label: 'Em progresso', icon: <AutorenewIcon style={{ width:18, height:18 }} /> },
    { key: 'done', label: 'Concluído', icon: <CheckCircleOutlineIcon style={{ width:18, height:18 }} /> }
  ];

  const grouped = useMemo(() => {
    const g = { planned: [], in_progress: [], done: [] };
    (roadmap || []).forEach(it => { if (g[it.status]) g[it.status].push(it); });
    return g;
  }, [roadmap]);

  const STATUS_COLOR_MAP = useMemo(() => ({
    planned: theme.palette.info.main,
    in_progress: theme.palette.warning.main,
    done: theme.palette.success.main
  }), [theme]);

  const PRIORIDADE_COLOR_MAP = useMemo(() => ({
    alta: theme.palette.warning.main,
    media: theme.palette.info.main,
    baixa: theme.palette.success.main
  }), [theme]);

  return (
    <Box display="flex" style={{ gap:16, flexWrap:'wrap' }}>
      {columns.map(col => {
        const headerColor = STATUS_COLOR_MAP[col.key] || theme.palette.grey[500];
        const headerBg = theme.palette.type === 'dark' ? hexToRgba(headerColor, 0.18) : hexToRgba(headerColor, 0.10);
        const headerBorder = headerColor;
        return (
          <Box key={col.key} flex={1} minWidth={240} display="flex" flexDirection="column" style={{ background: theme.palette.background.default, borderRadius: 10, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
            <Box
              role="heading"
              aria-level={3}
              aria-label={`${col.label}, ${grouped[col.key].length} itens`}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{
                padding: '6px 10px',
                background: headerBg,
                borderBottom: `2px solid ${headerBorder}`,
                backdropFilter: 'blur(2px)'
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap:6, minWidth:0 }}>
                <Box aria-hidden="true">{col.icon}</Box>
                <Typography variant="subtitle2" style={{ fontSize:13, fontWeight:600, whiteSpace:'nowrap' }}>{col.label}</Typography>
              </Box>
              <Chip size="small" label={grouped[col.key].length} style={{ background: headerColor, color: theme.palette.getContrastText(headerColor), height:20, fontWeight:600 }} />
            </Box>
            <Box display="flex" flexDirection="column" style={{ gap:8, padding:10, minHeight:60 }}>
              {grouped[col.key].map(it => {
                const base = PRIORIDADE_COLOR_MAP[it.prioridade] || theme.palette.grey[500];
                const tint = theme.palette.type === 'dark' ? hexToRgba(base, 0.20) : hexToRgba(base, 0.10);
                const hoverTint = theme.palette.type === 'dark' ? hexToRgba(base, 0.28) : hexToRgba(base, 0.16);
                return (
                  <Box key={it.id}
                    p={1}
                    borderRadius={8}
                    style={{
                      background: tint,
                      border: `1px solid ${base}`,
                      transition: 'background 120ms ease, box-shadow 120ms ease',
                      cursor: it.descricaoMarkdown ? 'pointer' : 'default'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = hoverTint; }}
                    onMouseLeave={e => { e.currentTarget.style.background = tint; }}
                  >
                    <Box display="flex" alignItems="center" style={{ gap:6 }}>
                      <Typography variant="body2" style={{ fontWeight:600, marginBottom: it.descricaoMarkdown ? 4 : 0 }}>{it.titulo}</Typography>
                      {isNovo(it.createdAt) && (
                        <Tooltip title="Novo (até 7 dias)"><StarIcon style={{ color: theme.palette.warning.main, fontSize: 16 }} /></Tooltip>
                      )}
                    </Box>
                    {it.descricaoMarkdown && <MarkdownWrapper>{it.descricaoMarkdown}</MarkdownWrapper>}
                  </Box>
                );
              })}
              {grouped[col.key].length === 0 && (
                <Typography variant="caption" style={{ opacity:0.6 }}>Nenhum.</Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const ChangelogModal = ({ open, onClose }) => {
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const { hydrateRoadmap, markRoadmapSeen } = useChangelog();
  // Lightbox state
  const [lbOpen, setLbOpen] = useState(false);
  const [lbImages, setLbImages] = useState([]);
  const [lbIndex, setLbIndex] = useState(0);
  // Listener simples para evento unpublish vindo via window (disparado do contexto)
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.action === 'release:unpublished') {
        // Ao cancelar publicação, fechamos modal conforme requisito
        if (typeof onClose === 'function') onClose();
      }
    };
    window.addEventListener('changelog-event', handler);
    return () => window.removeEventListener('changelog-event', handler);
  }, [onClose]);

  useEffect(() => {
    if (open && tab === 1) {
      if (typeof hydrateRoadmap === "function") hydrateRoadmap();
      if (typeof markRoadmapSeen === "function") markRoadmapSeen();
    }
  }, [open, tab, hydrateRoadmap, markRoadmapSeen]);

  // Bridge para abrir lightbox a partir dos cards dos itens
  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail;
      if (!detail || !Array.isArray(detail.images)) return;
      setLbImages(detail.images);
      setLbIndex(detail.index || 0);
      setLbOpen(true);
    };
    window.addEventListener('changelog-lightbox-open', handler);
    return () => window.removeEventListener('changelog-lightbox-open', handler);
  }, []);

  const closeLb = () => setLbOpen(false);
  const prevLb = () => setLbIndex(i => (lbImages.length ? (i - 1 + lbImages.length) % lbImages.length : 0));
  const nextLb = () => setLbIndex(i => (lbImages.length ? (i + 1) % lbImages.length : 0));

  useEffect(() => {
    if (!lbOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLb();
      if (e.key === 'ArrowLeft') prevLb();
      if (e.key === 'ArrowRight') nextLb();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lbOpen, lbImages.length]);
  return (
    <React.Fragment>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ style: { overflow:'hidden' } }}>
      <DialogTitle style={{ padding: '10px 16px', position:'relative' }}>
        <Box display="flex" alignItems="center" justifyContent="center" style={{ position:'relative' }}>
          <Typography variant="subtitle1" style={{ fontWeight:700, color: theme.palette.primary.main, textAlign:'center', flex:1 }}>
            Novidades & Roadmap
          </Typography>
          <IconButton aria-label="Fechar" size="small" onClick={onClose} style={{ position:'absolute', top:4, right:0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ padding:0 }}>
        <Box display="flex" flexDirection="column" style={{ height: '70vh', minHeight: 440 }}>
          <Box px={1.5} pt={0.5} style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Tabs value={tab} onChange={(_,v)=>setTab(v)} indicatorColor="primary" textColor="primary" variant="fullWidth" style={{ minHeight:38 }}>
              <Tab label="Novidades" style={{ minHeight:36 }} />
              <Tab label="Roadmap" style={{ minHeight:36 }} />
            </Tabs>
          </Box>
          <Box flex={1} p={1.5} pb={1} overflow="hidden" display="flex" flexDirection="column">
            {tab === 0 && <ReleasesSection />}
            {tab === 1 && (
              <Box flex={1} overflow="auto" pr={0.5} style={{ scrollbarGutter:'stable' }}>
                <RoadmapBoard />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
  </Dialog>

    {/* Lightbox Dialog */}
  <Dialog open={lbOpen} onClose={closeLb} maxWidth="lg" fullWidth>
      <DialogTitle disableTypography>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" style={{ fontWeight:700 }}>
            {lbImages[lbIndex]?.title || 'Anexo'} {lbImages.length > 1 ? `(${lbIndex+1}/${lbImages.length})` : ''}
          </Typography>
          <IconButton size="small" onClick={closeLb} aria-label="Fechar"><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" alignItems="center" justifyContent="center" position="relative">
          {lbImages.length > 1 && (
            <IconButton onClick={prevLb} style={{ position:'absolute', left: -8, top: '50%', transform:'translateY(-50%)' }} aria-label="Anterior"><ChevronLeftIcon /></IconButton>
          )}
          <img
            src={lbImages[lbIndex]?.src}
            alt={lbImages[lbIndex]?.title || 'Anexo'}
            style={{ maxWidth:'100%', maxHeight:'70vh', objectFit:'contain', display:'block', borderRadius:8 }}
          />
          {lbImages.length > 1 && (
            <IconButton onClick={nextLb} style={{ position:'absolute', right: -8, top: '50%', transform:'translateY(-50%)' }} aria-label="Próximo"><ChevronRightIcon /></IconButton>
          )}
        </Box>
      </DialogContent>
    </Dialog>
    </React.Fragment>
  );
};

export default ChangelogModal;
